/**
 * Every store in this lane (carolinaWorkflowStore, teamManagementStore,
 * customerFeedbackStore) throws a named *ConflictError when a mutation's
 * expectedVersion no longer matches -- the record was changed by someone
 * (or something) else since it was loaded. That's the real, valuable
 * protection: a stale write never silently overwrites newer data. But the
 * raw message ("X changed since it was loaded (expected version 1,
 * currently 2). Reload before retrying.") reads like a developer log line,
 * not something to show a legal professional. This turns any conflict
 * error into one honest, actionable sentence, and otherwise passes real
 * error messages through unchanged.
 */
const CONFLICT_ERROR_NAMES = new Set(['WorkflowConflictError', 'TeamManagementConflictError', 'CustomerFeedbackConflictError', 'AccountSettingsConflictError']);

/**
 * Founder UX rule (Issue #697 comment 6175523464): an UNKNOWN outcome must never be shown as a
 * plain failure with a blind "try again". A TypeError (e.g. `fetch` rejecting) or SyntaxError
 * (e.g. an unparseable reply) means the result is unconfirmed -- a write may already have been
 * committed. These are matched by name, so they can also be programming/shape errors; the copy
 * therefore asserts no cause. It also does not treat the data already on screen as confirmation
 * (a lost reply leaves stale local data visible): it asks for a fresh read (reload) before any
 * repeat, and support if the result still can't be told. Callers that perform reads (e.g.
 * downloads) share this copy, so it never mentions duplicates or business mutations. This helper
 * only words the message; it adds no retry or reconciliation of its own.
 */
export const UNKNOWN_OUTCOME_MESSAGE = "We couldn't confirm the result of this action. Don't repeat it yet: reload the page to get the latest saved information and check whether it already happened. If you still can't tell, contact support.";

function isUnknownOutcome(error: Error): boolean {
  return error.name === 'TypeError' || error.name === 'SyntaxError';
}

export function friendlyMutationError(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    if (CONFLICT_ERROR_NAMES.has(error.name)) {
      return "This was just updated — you're now looking at the latest version. Check it over and try again.";
    }
    if (isUnknownOutcome(error)) {
      return UNKNOWN_OUTCOME_MESSAGE;
    }
    return error.message;
  }
  return fallback;
}
